import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface DownloadHistoryItem {
  id: string;
  sequence_id: string;
  download_date: string;
  price_paid: number;
  status: 'completed' | 'failed';
  is_free: boolean;
  sequence: {
    id: string;
    title: string;
    name: string;
    image_url?: string;
    artist_name?: string;
    software_type?: string;
    file_url?: string;
    price?: number;
    duration?: number;
  };
}

export const useDownloadHistory = () => {
  const [downloads, setDownloads] = useState<DownloadHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchDownloadHistory = async () => {
    if (!user) {
      setDownloads([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // First, get paid purchases (simplified query)
      const { data: purchases, error: purchasesError } = await supabase
        .from('purchases')
        .select(`
          id,
          sequence_id,
          amount_paid,
          status,
          created_at
        `)
        .eq('user_id', user.id)
        .eq('status', 'completed')
        .order('created_at', { ascending: false });

      if (purchasesError) throw purchasesError;

      // Then, get free downloads from download_logs (simplified query)
      const { data: freeLogs, error: logsError } = await supabase
        .from('download_logs')
        .select(`
          id,
          sequence_id,
          created_at
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (logsError) throw logsError;

      // Get all unique sequence IDs from both purchases and free downloads
      const allSequenceIds = new Set<string>();
      (purchases || []).forEach(p => allSequenceIds.add(p.sequence_id));
      (freeLogs || []).forEach(l => allSequenceIds.add(l.sequence_id));

      // Fetch sequence details for all sequence IDs
      let sequencesData: any[] = [];
      if (allSequenceIds.size > 0) {
        const { data: sequences, error: sequencesError } = await supabase
          .from('sequences')
          .select(`
            id,
            title,
            name,
            image_url,
            software_type,
            file_url,
            price,
            duration,
            is_free,
            artist_id,
            artists (name)
          `)
          .in('id', Array.from(allSequenceIds));

        if (sequencesError) throw sequencesError;
        sequencesData = sequences || [];
      }

      // Create a map for quick sequence lookup
      const sequencesMap = new Map();
      sequencesData.forEach(seq => {
        sequencesMap.set(seq.id, seq);
      });

      // Combine and deduplicate
      const downloadHistory: DownloadHistoryItem[] = [];
      const seenSequenceIds = new Set<string>();

      // Add paid purchases first
      if (purchases) {
        purchases.forEach(purchase => {
          const sequence = sequencesMap.get(purchase.sequence_id);
          if (sequence && !seenSequenceIds.has(purchase.sequence_id)) {
            seenSequenceIds.add(purchase.sequence_id);
            downloadHistory.push({
              id: purchase.id,
              sequence_id: purchase.sequence_id,
              download_date: purchase.created_at,
              price_paid: purchase.amount_paid,
              status: 'completed',
              is_free: false,
              sequence: {
                id: sequence.id,
                title: sequence.title || sequence.name,
                name: sequence.name,
                image_url: sequence.image_url,
                artist_name: sequence.artists?.name,
                software_type: sequence.software_type,
                file_url: sequence.file_url,
                price: sequence.price,
                duration: sequence.duration,
              }
            });
          }
        });
      }

      // Add free downloads for sequences not already in the list
      if (freeLogs) {
        freeLogs.forEach(log => {
          const sequence = sequencesMap.get(log.sequence_id);
          if (sequence && !seenSequenceIds.has(log.sequence_id) && sequence.is_free) {
            seenSequenceIds.add(log.sequence_id);
            downloadHistory.push({
              id: log.id,
              sequence_id: log.sequence_id,
              download_date: log.created_at,
              price_paid: 0,
              status: 'completed',
              is_free: true,
              sequence: {
                id: sequence.id,
                title: sequence.title || sequence.name,
                name: sequence.name,
                image_url: sequence.image_url,
                artist_name: sequence.artists?.name,
                software_type: sequence.software_type,
                file_url: sequence.file_url,
                price: sequence.price,
                duration: sequence.duration,
              }
            });
          }
        });
      }

      // Sort by date (most recent first)
      downloadHistory.sort((a, b) => 
        new Date(b.download_date).getTime() - new Date(a.download_date).getTime()
      );

      setDownloads(downloadHistory);
    } catch (err) {
      console.error('Error fetching download history:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch download history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDownloadHistory();
  }, [user]);

  return {
    downloads,
    loading,
    error,
    refetch: fetchDownloadHistory
  };
};